import {
  Stack,
  Text,
  Image,
  HStack,
  Icon,
  Heading,
  Box,
  ImageProps,
} from '@chakra-ui/react';
import { Broker } from '@habity/types';
import { getBrokerPath, getTitle } from '@habity/services/brokers';
import { BrokerCardButtons } from './components/Buttons';
import { ListSkeleton } from '~/components/homes/list/components/ListSkeleton';
import { Link } from '@chakra-ui/next-js';
import { ICONS } from '~/constants';
import { getMunicipalityPath } from '@habity/services/municipalities';
import { useBrokerImage } from '~/hooks/useBrokerImage';
import { getBrokerNameParts } from '@habity/services';
import { useInView } from 'react-intersection-observer';

type BrokerCardListProps = {
  broker: Broker;
  customTitle?: string;
  isLoading?: boolean;
  showButtons?: boolean;
};

export function BrokerCardList({
  broker,
  customTitle,
  isLoading,
  showButtons = true,
}: BrokerCardListProps) {
  const { name, email, phone } = broker || {};

  // Check if this is a fallback broker
  // const isFallbackBroker = broker.result_type === 'fallback';

  // For fallback brokers, show special text instead of custom title
  const displayTitle = customTitle || getTitle(broker);

  const { avatarUrl, isImageMissing, setIsImageMissing } =
    useBrokerImage(broker);

  const { firstName, lastName } = getBrokerNameParts(name);

  if (isLoading) {
    return (
      <Stack gap="2" mt="2">
        {/* Title */}
        <ListSkeleton w="220px" h="14px" />

        {/* Name */}
        <ListSkeleton maxW="350px" h="32px" />

        {/* Photo */}
        <ListSkeleton w="100%" h="300px" />

        {/* Contact info */}
        <Stack mt="4">
          <ListSkeleton w="180px" h="14px" />
          <ListSkeleton w="300px" h="14px" />
        </Stack>

        {/* Buttons */}
        <HStack mt="3" gap="2">
          <ListSkeleton w="100%" h="32px" />
          <ListSkeleton w="100%" h="32px" />
        </HStack>
      </Stack>
    );
  }

  return (
    <Stack spacing="3">
      <Link href={getBrokerPath(broker)} _hover={{ textDecoration: 'none' }}>
        <Stack spacing="3">
          {/* Title, name, image */}
          <Stack spacing="0.5">
            <Text size="sm" fontWeight="light" color="blackAlpha.800">
              {displayTitle}
            </Text>

            <Heading size="xl">
              {firstName}{' '}
              <span style={{ fontStyle: 'italic' }}>{lastName}</span>
            </Heading>
          </Stack>

          {isImageMissing ? (
            <Box w="100%" h="300px" backgroundColor="blackAlpha.50" />
          ) : (
            <BrokerCardImage
              url={avatarUrl}
              name={name}
              title={displayTitle}
              onError={() => setIsImageMissing(true)}
            />
          )}
        </Stack>
      </Link>

      <HStack justifyContent="space-between" alignItems="flex-start">
        {/* Contact info */}
        <Stack gap="1" fontSize="sm" fontWeight="light" color="blackAlpha.800">
          <Link href={`tel:${phone}`}>
            <HStack alignItems="center" flexShrink="0">
              <Icon as={ICONS.PHONE} boxSize="4" color="blackAlpha.700" />
              <Text size="sm" fontWeight="light" flexShrink="0">
                {phone}
              </Text>
            </HStack>
          </Link>
          <Link href={`mailto:${email}?subject=Fråga till mäklare`}>
            <HStack alignItems="center" flexShrink="0">
              <Icon as={ICONS.MAIL} boxSize="4" color="blackAlpha.700" />
              <Text size="sm" fontWeight="light" flexShrink="0">
                {email}
              </Text>
            </HStack>
          </Link>
        </Stack>

        {/* Location: municipality name */}
        {broker.municipality?.name && (
          <Stack>
            <Link href={getMunicipalityPath(broker.municipality)}>
              <HStack alignItems="center" flexShrink="0" gap="1">
                <Icon
                  as={ICONS.LOCATION_PIN}
                  boxSize="4"
                  color="blackAlpha.700"
                />
                <Text
                  size="sm"
                  fontWeight="light"
                  flexShrink="0"
                  color="blackAlpha.700"
                >
                  {broker.municipality.name}
                </Text>
              </HStack>
            </Link>
          </Stack>
        )}
      </HStack>

      {/* Buttons */}
      {showButtons && (
        <Stack mt="2">
          <BrokerCardButtons
            broker={broker}
            variant="search"
            home={undefined}
          />
        </Stack>
      )}
    </Stack>
  );
}

interface BrokerCardImageProps {
  url: string;
  name: string;
  title: ImageProps['title'];
  onError: ImageProps['onError'];
}

function BrokerCardImage({ url, name, title, onError }: BrokerCardImageProps) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  return (
    <Box ref={ref} h="300px" backgroundColor="blackAlpha.50">
      {inView ? (
        <Image
          backgroundColor="blackAlpha.50"
          h="300px"
          objectFit="cover"
          objectPosition="top"
          loading="lazy"
          srcSet={`${url} 3x, ${url} 1x`}
          src={url}
          fallback={<ListSkeleton isLoaded={false} w="100%" h="100%" />}
          alt={name}
          title={title}
          onError={onError}
        />
      ) : (
        <ListSkeleton isLoaded={false} w="100%" h="100%" />
      )}
    </Box>
  );
}
